import { Link } from "@/lib/router-compat";
import { useSEO } from "@/hooks/useSEO";
import { ArrowLeft, Brain, CheckCircle } from "lucide-react";

import { ArticleTOC } from "@/components/resources/ArticleTOC";
import { ArticleJsonLd } from "@/components/resources/ArticleJsonLd";

const sections = [
  { id: "what-is-active-recall", title: "What is Active Recall?" },
  { id: "why-it-works", title: "Why Active Recall Works" },
  { id: "how-to-practice", title: "How to Practice Active Recall" },
  { id: "active-vs-passive", title: "Active Recall vs Passive Review" },
  { id: "key-takeaways", title: "Key Takeaways" },
];

const ActiveRecallGuide = () => {
  useSEO({
    title: "Active Recall: The Science of Effective Learning | System Atlas",
    description: "Learn how active recall works and why it's the most effective study technique. Evidence-based guide with practical implementation steps.",
    type: "article",
    alternates: [
      { hreflang: "en", href: "https://systematlas.fr/learn/active-recall" },
      { hreflang: "fr", href: "https://systematlas.fr/fr/learn/active-recall" },
      { hreflang: "x-default", href: "https://systematlas.fr/learn/active-recall" },
    ],
  });

  return (
    <>
      <ArticleJsonLd title="Active Recall: The Science of Effective Learning" description="Learn how active recall works and why it's the most effective study technique." publishedDate="2026-02-08" modifiedDate="2026-02-21" inLanguage="en" />
      <div className="container mx-auto px-6 py-12 max-w-3xl">
        <Link to="/learn" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 text-sm">
          <ArrowLeft className="w-4 h-4" />
          Back to Learning Guides
        </Link>

        <article>
          <h1 className="text-3xl md:text-4xl font-serif font-bold mb-6 tracking-tight">
            Active Recall: The Science of Effective Learning
          </h1>

          <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
            Active recall is a learning strategy that involves actively retrieving information from memory rather than passively reviewing it. Research consistently shows it outperforms traditional study methods.
          </p>

          <ArticleTOC sections={sections} />

          <section id="what-is-active-recall" className="mb-10">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <Brain className="w-5 h-5 text-primary" />
              What is Active Recall?
            </h2>
            <p className="text-muted-foreground mb-4 leading-relaxed">
              Active recall is the process of stimulating memory during learning by actively retrieving information. Instead of reading notes repeatedly, you close your materials and attempt to recall key concepts from memory.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              This contrasts with passive review methods like re-reading or highlighting, which create an illusion of learning without strengthening neural pathways.
            </p>
            <p className="text-muted-foreground mt-4 leading-relaxed">
              In practice, most students combine active recall with <Link to="/learn/spaced-repetition" className="text-primary underline underline-offset-2 hover:opacity-80">spaced repetition</Link> and quick self-tests built from their own material – for example by <Link to="/learn/ai-quiz-generator" className="text-primary underline underline-offset-2 hover:opacity-80">turning notes into MCQ quizzes</Link> or reviewing a <Link to="/learn/perfect-revision-sheet" className="text-primary underline underline-offset-2 hover:opacity-80">well-built revision sheet</Link>.
            </p>
          </section>

          <section id="why-it-works" className="mb-10">
            <h2 className="text-xl font-semibold mb-4">Why Active Recall Works</h2>
            <p className="text-muted-foreground mb-4 leading-relaxed">
              The effectiveness of active recall is supported by decades of cognitive science research:
            </p>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                <span className="text-muted-foreground">
                  <strong className="text-foreground">Strengthens memory traces:</strong> Each retrieval attempt reinforces the neural pathways associated with that information.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                <span className="text-muted-foreground">
                  <strong className="text-foreground">Identifies knowledge gaps:</strong> When you struggle to recall something, you immediately know what needs more study.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                <span className="text-muted-foreground">
                  <strong className="text-foreground">Improves transfer:</strong> Information retrieved actively is more readily available in different contexts.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                <span className="text-muted-foreground">
                  <strong className="text-foreground">Testing effect:</strong> The act of taking a test improves learning more than spending equivalent time studying.
                </span>
              </li>
            </ul>
          </section>

          <section id="how-to-practice" className="mb-10">
            <h2 className="text-xl font-semibold mb-4">How to Practice Active Recall</h2>
            <div className="space-y-4">
              {[
                { step: "1. Close your materials", desc: "After studying a section, close your book or notes. Don't peek while trying to recall." },
                { step: "2. Write or speak what you remember", desc: "Attempt to write down or verbally explain the key concepts, definitions, and relationships." },
                { step: "3. Check your accuracy", desc: "Compare what you recalled against the source material. Note gaps and errors." },
                { step: "4. Focus on weak areas", desc: "Spend more time on concepts you struggled to recall. These are your knowledge gaps." },
              ].map((item) => (
                <div key={item.step} className="p-4 border border-border/60 rounded-lg">
                  <h3 className="font-medium mb-2">{item.step}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
            <p className="text-muted-foreground mt-4 leading-relaxed text-sm">
              You can automate steps 2-4 by <Link to="/learn/pdf-to-sheet" className="text-primary underline underline-offset-2 hover:opacity-80">converting a PDF into a revision sheet</Link> and then running an <Link to="/learn/ai-spaced-repetition" className="text-primary underline underline-offset-2 hover:opacity-80">AI-scheduled quiz loop</Link> that surfaces the exact concepts you failed to recall.
            </p>
          </section>

          <section id="active-vs-passive" className="mb-10">
            <h2 className="text-xl font-semibold mb-4">Active Recall vs Passive Review</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 font-medium">Active Recall</th>
                    <th className="text-left py-3 px-4 font-medium">Passive Review</th>
                  </tr>
                </thead>
                <tbody className="text-muted-foreground">
                  <tr className="border-b border-border/50"><td className="py-3 px-4">Testing yourself without notes</td><td className="py-3 px-4">Re-reading highlighted text</td></tr>
                  <tr className="border-b border-border/50"><td className="py-3 px-4">Answering practice questions</td><td className="py-3 px-4">Copying notes</td></tr>
                  <tr className="border-b border-border/50"><td className="py-3 px-4">Teaching concepts from memory</td><td className="py-3 px-4">Watching lecture recordings</td></tr>
                  <tr><td className="py-3 px-4">Creating questions then answering them</td><td className="py-3 px-4">Reading summaries</td></tr>
                </tbody>
              </table>
            </div>
          </section>

          <section id="key-takeaways" className="p-6 bg-primary/5 border border-primary/15 rounded-xl">
            <h2 className="text-lg font-medium mb-3">Key Takeaways</h2>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>• Active recall strengthens memory more than passive review</li>
              <li>• The struggle of retrieval is what creates learning</li>
              <li>• Testing yourself identifies knowledge gaps early</li>
              <li>• Combine with spaced repetition for optimal results</li>
              <li>• Even failed recall attempts improve long-term retention</li>
            </ul>
          </section>
        </article>

        <nav className="mt-12 pt-8 border-t border-border/40">
          <h3 className="font-medium mb-4">Related Learning Guides</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <Link to="/learn/ai-quiz-generator" className="p-4 border border-border/60 rounded-lg hover:border-primary/40 transition-colors">
              <h4 className="font-medium mb-1 text-sm">Generate Quizzes with AI</h4>
              <p className="text-xs text-muted-foreground">Turn your notes into targeted MCQ quizzes</p>
            </Link>
            <Link to="/learn/ai-spaced-repetition" className="p-4 border border-border/60 rounded-lg hover:border-primary/40 transition-colors">
              <h4 className="font-medium mb-1 text-sm">AI-Powered Spaced Repetition</h4>
              <p className="text-xs text-muted-foreground">SM-2 scheduling meets active recall</p>
            </Link>
            <Link to="/learn/revision-sheets" className="p-4 border border-border/60 rounded-lg hover:border-primary/40 transition-colors">
              <h4 className="font-medium mb-1 text-sm">Creating Effective Revision Sheets</h4>
              <p className="text-xs text-muted-foreground">Structure your notes for maximum retention</p>
            </Link>
            <Link to="/learn/blurting-method" className="p-4 border border-border/60 rounded-lg hover:border-primary/40 transition-colors">
              <h4 className="font-medium mb-1 text-sm">The Blurting Method</h4>
              <p className="text-xs text-muted-foreground">A raw, powerful form of active recall</p>
            </Link>
            <Link to="/learn/atlas-vs-anki" className="p-4 border border-border/60 rounded-lg hover:border-primary/40 transition-colors">
              <h4 className="font-medium mb-1 text-sm">Atlas vs Anki</h4>
              <p className="text-xs text-muted-foreground">Flashcards vs structured sheets for recall</p>
            </Link>
            <Link to="/learn/ai-exam-prep" className="p-4 border border-border/60 rounded-lg hover:border-primary/40 transition-colors">
              <h4 className="font-medium mb-1 text-sm">AI Exam Preparation</h4>
              <p className="text-xs text-muted-foreground">Chain sheets, quizzes and gap analysis</p>
            </Link>
          </div>
        </nav>
      </div>
    </>
  );
};

export default ActiveRecallGuide;
